import React, {useState, useEffect} from 'react'
import {
  Stack,
  Box,
  Tooltip,
  CircularProgress,
  Button,
  Switch,
  Select,
  MenuItem,
  useTheme,
  Typography,
} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import LanguageSelector from './LanguageSelector'
import translationApi from '../../services/api/translations/translationService'
import textToSpeechApi from '../../services/api/textToSpeach/textToSpeach'
import WbButton from '../../components/common/WbButton'
import WidgitPointToggle from '../../components/common/WidgitPointToggle'

export default function WelcomeScreen({
  setSurveyStart,
  surveyData,
  language,
  setLanguage,
  sourcelanguage,
  setSourceLanguage,
  setTtsEnabled,
  setVoice,
}) {
  const [audioUrl, setAudioUrl] = useState(null)
  const [audioReady, setAudioReady] = useState(false)
  const [localTtsEnabled, setLocalTtsEnabled] = useState(false)
  const [localVoice, setLocalVoice] = useState('Female')
  const [ttsLoading, setTtsLoading] = useState(false)
  const [surveyName, setSurveyName] = useState(surveyData?.surveyName)
  const [schoolName, setSchoolName] = useState(surveyData?.schoolName)
  const hasSchoolName =
    surveyData?.schoolName && surveyData.schoolName.trim() !== ''
  const WELCOME_MESSAGE = `Welcome to the survey named  ${surveyData?.surveyName}${hasSchoolName ? ` for ${surveyData.schoolName.trim()}` : ''}. Please answer the questions as honestly as possible. Click on Start Survey button to begin.`
  const [h1, setH1] = useState('Welcome to the survey')
  const [h2, setH2] = useState(
    'Please answer the questions as honestly as possible.'
  )
  const [buttonText, setButtonText] = useState('Start Survey')
  const [loading, setLoading] = useState(false)
  const theme = useTheme()

  const [widgitEnabled, setWidgitEnabled] = useState(() => {
    const isGrade4OrBelow = surveyData?.isLessThanGrade5
    const isSurveyWidgitEnabled = surveyData?.isWidgitEnabled
    if (isGrade4OrBelow) return true
    if (isSurveyWidgitEnabled === false) {
      document.cookie = 'point_enabled=false; max-age=0; path=/'
      return false
    }
    const cookieValue = document.cookie
      .split('; ')
      .find(row => row.startsWith('point_enabled='))
    if (!cookieValue) {
      document.cookie = `point_enabled=${isSurveyWidgitEnabled ? 'true' : 'false'}; max-age=${90 * 24 * 60 * 60}; path=/`
      return isSurveyWidgitEnabled === true
    }
    if (cookieValue) return cookieValue.split('=')[1] === 'true'
    return isSurveyWidgitEnabled === true
  })

  const handleWidgitToggle = newState => {
    if (!surveyData?.isLessThanGrade5) {
      setWidgitEnabled(newState)
      document.cookie = `point_enabled=${newState ? 'true' : 'false'}; max-age=${90 * 24 * 60 * 60}; path=/`
      setPointWidgetState(newState)
    }
  }

  useEffect(() => {
    const cookieValue = document.cookie
      .split('; ')
      .find(row => row.startsWith('point_enabled='))
    if (cookieValue && cookieValue.split('=')[1] === 'true') {
      if (!document.body.classList.contains('point_enabled')) {
        document.body.classList.add('point_enabled')
        if (window.Point?.togglePoint) window.Point.togglePoint()
      }
    } else {
      if (document.body.classList.contains('point_enabled')) {
        document.body.classList.remove('point_enabled')
        if (window.Point?.togglePoint) window.Point.togglePoint()
      }
    }
  }, [widgitEnabled])

  const isToggleDisabled = surveyData?.isLessThanGrade5

  function setPointWidgetState(shouldEnable) {
    if (shouldEnable) {
      document.body.classList.add('point_enabled')
      if (window.Point?.togglePoint) window.Point.togglePoint()
    } else {
      window.Point.togglePoint()
      document.body.classList.remove('point_enabled')
    }
  }

  useEffect(() => {
    setLoading(true)
    // Always use English as source language and English values for translation
    const englishSurveyName = surveyData?.surveyName || ''
    const englishSchoolName = surveyData?.schoolName || ''
    const englishH1 = 'Welcome to the survey'
    const englishH2 = 'Please answer the questions as honestly as possible.'
    const englishButtonText = 'Start Survey'
    translationApi
      .getTranslationsForText(language, 'en', [
        englishSurveyName,
        englishSchoolName,
        englishH1,
        englishH2,
        englishButtonText,
      ])
      .then(async translations => {
        setSurveyName(translations[0])
        setSchoolName(translations[1])
        setH1(translations[2])
        setH2(translations[3])
        setButtonText(translations[4])
        if (localTtsEnabled) {
          setTtsLoading(true)
          // Stop any currently playing audio before proceeding
          if (
            window.__welbeeCurrentAudio &&
            typeof window.__welbeeCurrentAudio.pause === 'function'
          ) {
            try {
              window.__welbeeCurrentAudio.pause()
              window.__welbeeCurrentAudio.currentTime = 0
            } catch (e) {}
            window.__welbeeCurrentAudio = null
          }
          try {
            const audioBlob = await textToSpeechApi.getWelcomeTextToSpeech({
              text: WELCOME_MESSAGE,
              languageCode: language,
              surveyId: surveyData?.surveyId,
              gender: localVoice,
            })
            const url = URL.createObjectURL(audioBlob)
            setAudioUrl(url)
            setAudioReady(true)
          } catch (err) {
            setAudioReady(false)
            setAudioUrl(null)
            console.error('Text-to-speech audio fetch failed:', err)
          }
          setTtsLoading(false)
        } else {
          setAudioReady(false)
          setAudioUrl(null)
        }
      })
      .finally(() => setLoading(false))
  }, [
    language,
    localTtsEnabled,
    localVoice,
    WELCOME_MESSAGE,
    buttonText,
    h1,
    h2,
    schoolName,
    sourcelanguage,
    surveyData?.surveyId,
    surveyName,
  ])

  useEffect(() => {
    if (localTtsEnabled && audioReady && audioUrl) {
      // Stop any currently playing audio before playing new one
      if (
        window.__welbeeCurrentAudio &&
        typeof window.__welbeeCurrentAudio.pause === 'function'
      ) {
        try {
          window.__welbeeCurrentAudio.pause()
          window.__welbeeCurrentAudio.currentTime = 0
        } catch (e) {}
        window.__welbeeCurrentAudio = null
      }
      const audio = new window.Audio(audioUrl)
      window.__welbeeCurrentAudio = audio
      audio.play().catch(() => {})
    }
  }, [localTtsEnabled, audioReady, audioUrl])

  return (
    <Box id="survey-content" sx={{position: 'relative'}}>
      {/* Loader overlay while TTS audio loads */}
      {localTtsEnabled && ttsLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.4)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{position: 'relative', width: 80, height: 80}}>
            <CircularProgress
              size={80}
              thickness={5}
              sx={{color: '#43a047', position: 'absolute', top: 0, left: 0}}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: 80,
                height: 80,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PlayArrowIcon
                sx={{
                  fontSize: 48,
                  color: '#fff',
                  background: '#43a047',
                  borderRadius: '50%',
                  p: 1.25,
                }}
              />
            </Box>
          </Box>
        </Box>
      )}
      <Box className="survey-header">
        <Stack direction="row" spacing={1} alignItems="center">
          <LanguageSelector
            value={language}
            onChange={(newLang, dir) => {
              setSourceLanguage(language)
              setLanguage(newLang)
              document.documentElement.setAttribute('lang', newLang)
              document.documentElement.setAttribute('dir', dir || 'ltr')
            }}
          />
          <Box
            sx={{
              border: `1px solid ${theme.palette.mode === 'dark' ? '#494949' : '#B8C1C4'}`,
              borderRadius: '4px',
              padding: '4px 8px',
              display: 'flex',
              alignItems: 'center',
              position: 'relative',
              minWidth: 120,
              height: 40,
            }}
            //className="themed-box"
          >
            <Box
              sx={{
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[100]
                    : theme.palette.text.primary,
                position: 'absolute',
                top: -8,
                left: 12,
                background:
                  theme.palette.mode === 'dark' ? '#121212' : '#F0FBFF',
                px: 1,
                fontSize: 10,
                fontWeight: 'bold',
              }}
            >
              Text To Speech
            </Box>
            <span
              style={{display: 'flex', alignItems: 'center', width: '100%'}}
            >
              <Typography style={{marginRight: 4}}>Off</Typography>
              <Switch
                size="small"
                checked={localTtsEnabled}
                onChange={e => setLocalTtsEnabled(e.target.checked)}
                color="primary"
              />
              <Typography style={{marginLeft: 4, marginRight: 12}}>
                On
              </Typography>
              {localTtsEnabled && (
                <Select
                  value={localVoice}
                  onChange={e => setLocalVoice(e.target.value)}
                  sx={{minWidth: 100, height: 32}}
                  size="small"
                >
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                </Select>
              )}
              {localTtsEnabled && audioReady && (
                <Tooltip title="Play Welcome Audio">
                  <span>
                    <Button
                      onClick={() => {
                        // Stop any currently playing audio before playing new one
                        if (
                          window.__welbeeCurrentAudio &&
                          typeof window.__welbeeCurrentAudio.pause ===
                            'function'
                        ) {
                          try {
                            window.__welbeeCurrentAudio.pause()
                            window.__welbeeCurrentAudio.currentTime = 0
                          } catch (e) {}
                          window.__welbeeCurrentAudio = null
                        }
                        if (audioUrl) {
                          const audio = new window.Audio(audioUrl)
                          window.__welbeeCurrentAudio = audio
                          audio.play().catch(() => {})
                        }
                      }}
                      sx={{
                        minWidth: 0,
                        padding: 0,
                        ml: 1,
                        background: '#43a047',
                        borderRadius: '50%',
                        transition: 'background 0.2s',
                        '&:hover': {
                          background: '#1b5e20',
                        },
                      }}
                    >
                      <PlayArrowIcon sx={{color: '#fff'}} />
                    </Button>
                  </span>
                </Tooltip>
              )}
            </span>
          </Box>
          <Tooltip
            title={
              isToggleDisabled
                ? 'Widgit is automatically enabled for students in Grade 4 or below'
                : 'Toggle Widgit symbols visibility'
            }
          >
            <Box>
              <WidgitPointToggle
                onToggle={handleWidgitToggle}
                enabled={widgitEnabled}
                disabled={isToggleDisabled}
              />
            </Box>
          </Tooltip>
        </Stack>
      </Box>
      {loading ? (
        <Box className="welcome">
          <CircularProgress />
        </Box>
      ) : (
        <Box className="welcome">
          <Box>
            <Typography variant="h1">{surveyName}</Typography>
            <Typography variant="h3">{schoolName}</Typography>
            {/* <h1>{surveyName}</h1>
            <h3>{schoolName}</h3> */}
          </Box>
          <Box>
            <Typography variant="h2">{h1}</Typography>
            <Typography variant="body1">{h2}</Typography>
            {/* <h2>{h1}</h2>
            <p>{h2}</p> */}
          </Box>
          <Button
            onClick={() => {
              if (typeof setTtsEnabled === 'function')
                setTtsEnabled(localTtsEnabled)
              if (typeof setVoice === 'function') setVoice(localVoice)
              setSurveyStart(true)
            }}
          >
            {buttonText}
          </Button>
        </Box>
      )}
    </Box>
  )
}
